import { lazy, Suspense } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAccount } from '../account/accountStore.ts';
import { useFlag } from '../meta/liveStore.ts';
import { onlineAvailable } from '../online/onlineConnection.ts';
import { useAppStore } from '../state/appStore.ts';
import { Badge, Icon, Logo, MenuTile, Panel } from '../ui/index.ts';
import styles from './Home.module.css';

// PixiJS is heavy: it loads after the first paint so the initial bundle stays small.
const CityBackdrop = lazy(() => import('../game/CityBackdrop.tsx'));

export function Home() {
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const webgl = useAppStore((s) => s.webglAvailable);
  const accountAvailable = useAccount((s) => s.setup.available);
  const onlineOpen = accountAvailable && onlineAvailable();
  const dailyOn = useFlag('daily');
  const dailyOpen = onlineOpen && dailyOn;
  const soon = <Badge tone="amber">{tc('soon')}</Badge>;

  return (
    <main id="main" tabIndex={-1} className={styles['home']}>
      <div className={styles['backdrop']} aria-hidden="true">
        <div className={styles['skylineFallback']} />
        {webgl ? (
          <Suspense fallback={null}>
            <CityBackdrop />
          </Suspense>
        ) : null}
      </div>

      <header className={styles['hero']}>
        <Logo label={tc('appName')} />
        <p className={styles['subtitle']}>{t('subtitle')}</p>
      </header>

      <aside className={styles['banner']} aria-label={t('banner.title')}>
        <Panel as="div" className={styles['bannerPanel']}>
          <Icon name="spark" size="1.75rem" />
          <div className={styles['bannerText']}>
            <strong className={styles['bannerTitle']}>{t('banner.title')}</strong>
            <span className={styles['bannerBody']}>{t('banner.body')}</span>
          </div>
          {dailyOpen ? (
            <Link to="/daily" className={styles['bannerLink']}>
              {t('banner.open')}
            </Link>
          ) : (
            soon
          )}
        </Panel>
      </aside>

      <nav className={styles['menu']} aria-label={tc('appName')}>
        <MenuTile
          primary
          index={0}
          to="/play"
          icon="play"
          title={t('menu.play')}
          hint={t('menu.playHint')}
        />
        <MenuTile
          index={1}
          to={onlineOpen ? '/online' : '/soon/online'}
          icon="online"
          title={t('menu.online')}
          hint={t('menu.onlineHint')}
          badge={onlineOpen ? undefined : soon}
        />
        <MenuTile
          index={2}
          to={accountAvailable ? '/account' : '/soon/profile'}
          icon="profile"
          title={t('menu.profile')}
          hint={t('menu.profileHint')}
          badge={accountAvailable ? undefined : soon}
        />
        <MenuTile
          index={3}
          to={accountAvailable ? '/collection' : '/soon/collection'}
          icon="collection"
          title={t('menu.collection')}
          hint={t('menu.collectionHint')}
          badge={accountAvailable ? undefined : soon}
        />
        <MenuTile
          index={4}
          to={accountAvailable ? '/leaderboards' : '/soon/leaderboards'}
          icon="trophy"
          title={t('menu.leaderboards')}
          hint={t('menu.leaderboardsHint')}
          badge={accountAvailable ? undefined : soon}
        />
        <MenuTile
          index={5}
          to="/settings"
          icon="settings"
          title={t('menu.settings')}
          hint={t('menu.settingsHint')}
        />
        <MenuTile
          index={6}
          to={accountAvailable ? '/news' : '/soon/news'}
          icon="news"
          title={t('menu.news')}
          hint={t('menu.newsHint')}
          badge={accountAvailable ? undefined : soon}
        />
      </nav>

      <footer className={styles['footer']}>{tc('version', { version: __APP_VERSION__ })}</footer>
    </main>
  );
}
